import { useEffect, useMemo, useRef } from "react";
import { styled } from "styled-components";
import { useDebounce } from "use-debounce";

import type { Dimension } from "api/api.types";
import { dimensionsApiSlice } from "api/dimensionApi.slice";
import { ExpandableContent, InfinityLoader, SectionTitleUnderlined } from "common/components";
import { createScrollbarStyle } from "common/styles";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import { DimensionItem } from "domains/createMapDetail/components/keyIssueList/DimensionItem";
import {
  getCurrentMapDimensionIds,
  getExistingDimensionIds,
  getLocalImage,
  getNewCustomDimensions,
} from "domains/createMapDetail/selectors";
import { useDimensions } from "domains/dimension/useDimensions";

const TopicList = styled.div`
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding-right: 10px;

  ${createScrollbarStyle()}
`;

const StyledTitle = styled(SectionTitleUnderlined)`
  margin-top: 1rem;
`;

type DimensionsListProps = { className?: string; isLight?: boolean; onEditKeyIssueSelect?: (id: string) => void };

export const DimensionsList = ({ onEditKeyIssueSelect, className }: DimensionsListProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const search = useSelector((state) => state.dimension.search) ?? "";
  const issueIdsOnMap = useSelector(getCurrentMapDimensionIds);
  const existingDimensionIds = useSelector(getExistingDimensionIds);
  const localImage = useSelector(getLocalImage);
  const newCustomDimensions = useSelector(getNewCustomDimensions);

  const translate = useTranslate();

  const existingDimensions = useDimensions(existingDimensionIds);
  const [debouncedSearch] = useDebounce(search, 1000);

  const {
    data: paginatedInitialDimensions,
    isFetching: areInitialDimensionFetching,
    fetchNextPage: fetchNextInitialResultsPage,
  } = dimensionsApiSlice.endpoints.getDimensions.useInfiniteQuery();
  const {
    data: paginatedDimensionSearchResults,
    isFetching: areDimensionSearchResultsFetching,
    fetchNextPage: fetchNextSearchResultsPage,
  } = dimensionsApiSlice.endpoints.searchDimensions.useInfiniteQuery(
    {
      search: debouncedSearch,
    },
    { skip: debouncedSearch === "" },
  );

  const loadNextPage = async () => {
    if (debouncedSearch !== "") {
      await fetchNextSearchResultsPage();
      return;
    }

    await fetchNextInitialResultsPage();
  };

  const initialDimensions = paginatedInitialDimensions?.pages.flatMap((page) => page.dimensions) ?? [];
  const dimensionsSearchResults = paginatedDimensionSearchResults?.pages.flat();

  const customDimensions = [
    ...existingDimensions.map(({ dimension }) => dimension).filter((dimension) => dimension.is_custom_dimension),
    ...newCustomDimensions,
  ];

  const dimensions = debouncedSearch || debouncedSearch !== "" ? dimensionsSearchResults : initialDimensions;

  // NOTE: Short term solution to fixing duplicates that are returned from the BE
  // Long term solution is in this ticket: https://www.notion.so/The-search-on-Discover-page-shows-more-results-in-the-UI-than-it-should-1c2cf2e17a4e80528ec5e14acd860ad8?source=copy_link
  const dimensionsWithoutDuplications = useMemo(
    () =>
      dimensions?.reduce((acc: Dimension[], currentDimension) => {
        const existingDimension = acc.find((item) => item.id === currentDimension.id);

        if (!existingDimension) return acc.concat([currentDimension]);

        return acc;
      }, []),
    [dimensions],
  );

  const scrollToTop = () => {
    containerRef.current?.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    scrollToTop();
  }, [debouncedSearch]);

  return (
    <TopicList ref={containerRef} className={className}>
      <ExpandableContent isActive={customDimensions.length !== 0}>
        <StyledTitle>{translate("app.create.my_key_issues")}</StyledTitle>
        {customDimensions.map((dimension) => (
          <DimensionItem
            description={dimension.description}
            id={dimension.id}
            image_url={dimension.image_url ?? localImage ?? null}
            is_custom_dimension={dimension.is_custom_dimension}
            isOnMap={issueIdsOnMap.some((issueId) => issueId === dimension.id)}
            key={dimension.id}
            name={dimension.name}
            onEditSelect={onEditKeyIssueSelect}
            parent_topic_id={dimension.parent_topic_id}
          />
        ))}
      </ExpandableContent>

      {customDimensions.length !== 0 && <StyledTitle>{translate("app.create.all_key_issues")}</StyledTitle>}
      <InfinityLoader
        hasNextPage={dimensions === undefined || dimensions.length >= 20}
        isPending={areInitialDimensionFetching || areDimensionSearchResultsFetching}
        loadNextPage={loadNextPage}
      >
        {dimensionsWithoutDuplications?.map((topic) => (
          <DimensionItem
            {...topic}
            dataTestId={`dimension-list-item-${topic.id}-add-button`}
            isOnMap={issueIdsOnMap.some((id) => id === topic.id)}
            key={topic.id}
          />
        ))}
      </InfinityLoader>
    </TopicList>
  );
};
